/**
 * An offline MapLibre style (design §9): tiles from an imported map package,
 * fonts and icons bundled with the UI (public/map), nothing from the network.
 *
 * @module
 */
import { layers, namedFlavor } from "@protomaps/basemaps";
import type { StyleSpecification } from "maplibre-gl";
import type { Schemas } from "../api/client";
import { safeHtml } from "../trustedTypes";

export type MapSet = Schemas["MapSet"];

export type Bounds = [number, number, number, number];

export function mapBounds(m: MapSet): Bounds {
  const [w = -180, s = -85, e = 180, n = 85] = m.bounds;
  return [w, s, e, n];
}

/**
 * MapLibre fetches from a worker, which needs absolute URLs. Not `new URL()`
 * on the whole path: it would percent-encode the `{z}`/`{range}` placeholders.
 */
export const abs = (path: string) =>
  new URL("/", window.location.href).href.replace(/\/$/, "") + path;

export function offlineStyle(
  map: MapSet | null,
  dark: boolean,
): StyleSpecification {
  const flavour = dark ? "dark" : "light";
  const style: StyleSpecification = {
    version: 8,
    glyphs: abs("/map/fonts/{fontstack}/{range}.pbf"),
    sprite: abs(`/map/sprites/v4/${flavour}`),
    sources: {},
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": dark ? "#1e1e1e" : "#f2efe9" },
      },
    ],
  };
  if (!map) return style;
  const tiles = [abs(`/api/maps/${map.id}/tiles/{z}/{x}/{y}`)];
  const common = {
    tiles,
    minzoom: map.min_zoom,
    maxzoom: map.max_zoom,
    bounds: mapBounds(map),
    // MapLibre renders credits as HTML; its own sanitiser had a bypass
    // (GHSA-jrc7-96c5-q579), so they're cut down here first.
    ...(map.attribution ? { attribution: safeHtml(map.attribution) } : {}),
  };
  if (map.tile_type === "mvt") {
    style.sources["basemap"] = { type: "vector", ...common };
    style.layers = layers("basemap", namedFlavor(flavour), { lang: "en" });
  } else {
    style.sources["basemap"] = { type: "raster", tileSize: 256, ...common };
    style.layers.push({ id: "basemap", type: "raster", source: "basemap" });
  }
  return style;
}
